import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import {
  listBlogs,
  createBlog,
  updateBlog,
  toggleBlog,
  deleteBlog,
  normalizeBlog,
} from './services/blogService';

const Container = styled.div`
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 1.5rem 2rem;
  margin-bottom: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 1rem;
  margin-bottom: 1.5rem;
  justify-content: space-between;
`;

const ToolbarLeft = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  flex: 1;
  min-width: 0;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 160px;
  flex: 1;
  max-width: 280px;
`;

const ToolbarLabel = styled.label`
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(255, 255, 255, 0.65);
`;

const SearchInput = styled.input`
  padding: 0.65rem 0.85rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(0, 0, 0, 0.2);
  color: #fff;
  font-size: 0.9rem;
  width: 100%;
  box-sizing: border-box;

  &::placeholder {
    color: rgba(255, 255, 255, 0.45);
  }
  &:focus {
    outline: none;
    border-color: rgba(102, 126, 234, 0.8);
  }
`;

const FilterSelect = styled.select`
  padding: 0.65rem 0.85rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(0, 0, 0, 0.25);
  color: #fff;
  font-size: 0.9rem;
  min-width: 140px;

  option {
    color: #1a202c;
  }
  &:focus {
    outline: none;
    border-color: rgba(102, 126, 234, 0.8);
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const PageTitle = styled.h2`
  color: #fff;
  font-size: 1.35rem;
  font-weight: 600;
  margin: 0;
  letter-spacing: -0.02em;
`;

const Button = styled.button`
  padding: 10px 20px;
  background: rgba(102, 126, 234, 0.95);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: #5a67d8;
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  padding: 8px 14px;
  background: transparent;
  color: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.08);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DangerButton = styled.button`
  padding: 8px 14px;
  background: transparent;
  color: #feb2b2;
  border: 1px solid rgba(254, 178, 178, 0.5);
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: rgba(229, 62, 62, 0.25);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TableWrap = styled.div`
  width: 100%;
  overflow-x: auto;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const Table = styled.table`
  width: 100%;
  min-width: 960px;
  border-collapse: collapse;
  background: rgba(0, 0, 0, 0.15);
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 14px;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(255, 255, 255, 0.55);
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(0, 0, 0, 0.2);
`;

const Td = styled.td`
  padding: 12px 14px;
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.92);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  vertical-align: middle;
`;

const Tr = styled.tr`
  &:hover td {
    background: rgba(255, 255, 255, 0.04);
  }
`;

const Thumbnail = styled.img`
  width: 80px;
  height: 45px;
  object-fit: cover;
  border-radius: 6px;
  background: rgba(102, 126, 234, 0.2);
`;

const StatusPill = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: ${(p) =>
    p.$active
      ? 'rgba(72, 187, 120, 0.25)'
      : 'rgba(237, 137, 54, 0.25)'};
  color: ${(p) => (p.$active ? '#9ae6b4' : '#fbd38d')};
  border: 1px solid
    ${(p) =>
      p.$active
        ? 'rgba(72, 187, 120, 0.45)'
        : 'rgba(237, 137, 54, 0.45)'};
`;

const ActionsCell = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Message = styled.div`
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 1rem;
  font-size: 0.875rem;
  font-weight: 500;

  ${({ $success }) =>
    $success &&
    `
    background: rgba(72, 187, 120, 0.2);
    color: #c6f6d5;
    border: 1px solid rgba(72, 187, 120, 0.35);
  `}
  ${({ $error }) =>
    $error &&
    `
    background: rgba(245, 101, 101, 0.15);
    color: #fed7d7;
    border: 1px solid rgba(245, 101, 101, 0.35);
  `}
`;

const Modal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalCard = styled.div`
  background: #f7fafc;
  border-radius: 12px;
  max-width: 720px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  padding: 1.75rem;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.35);
`;

const ModalTitle = styled.h3`
  margin: 0 0 1.25rem;
  font-size: 1.2rem;
  color: #1a202c;
  font-weight: 600;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const Label = styled.label`
  font-size: 0.8rem;
  font-weight: 600;
  color: #4a5568;
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid #cbd5e0;
  border-radius: 8px;
  font-size: 0.875rem;
  box-sizing: border-box;
  width: 100%;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.15);
  }
`;

const TextArea = styled.textarea`
  padding: 10px 12px;
  border: 1px solid #cbd5e0;
  border-radius: 8px;
  font-size: 0.875rem;
  min-height: 100px;
  resize: vertical;
  font-family: inherit;
  box-sizing: border-box;
  width: 100%;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.15);
  }
`;

const FieldError = styled.span`
  font-size: 0.75rem;
  color: #c53030;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.5rem;
  flex-wrap: wrap;
`;

const GhostButton = styled.button`
  padding: 10px 18px;
  border-radius: 8px;
  border: 1px solid #cbd5e0;
  background: #fff;
  color: #2d3748;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;

  &:hover {
    background: #edf2f7;
  }
`;

const VideoTypeSelect = styled.select`
  padding: 10px 12px;
  border: 1px solid #cbd5e0;
  border-radius: 8px;
  font-size: 0.875rem;
  box-sizing: border-box;
  width: 100%;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.15);
  }
`;

const FileInputWrapper = styled.div`
  position: relative;
`;

const FileInput = styled.input`
  display: none;
`;

const FileInputLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 10px 16px;
  background: #667eea;
  color: white;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;
  transition: background 0.2s;

  &:hover {
    background: #5a67d8;
  }
`;

const FileInfo = styled.div`
  margin-top: 0.5rem;
  font-size: 0.8rem;
  color: #4a5568;
`;

const PrimaryModalButton = styled.button`
  padding: 10px 18px;
  border-radius: 8px;
  border: none;
  background: #5a67d8;
  color: #fff;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #4c51bf;
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const ThumbnailPreview = styled.img`
  width: 100%;
  max-width: 320px;
  height: auto;
  border-radius: 8px;
  margin-top: 0.5rem;
  background: rgba(102, 126, 234, 0.1);
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2.5rem 1rem;
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.9rem;
`;

const DEBOUNCE_MS = 320;

function extractYouTubeId(url) {
  if (!url) return null;
  
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /^([a-zA-Z0-9_-]{11})$/
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }

  return null;
}

function generateThumbnailUrl(videoId) {
  if (!videoId) return null;
  return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
}

function validateForm(values) {
  const errors = {};
  const title = String(values.title || '').trim();
  if (title.length < 2) errors.title = 'Title is required (min 2 characters).';

  const shortDescription = String(values.short_description || '').trim();
  if (shortDescription.length < 10) {
    errors.short_description = 'Short description is required (min 10 characters).';
  }

  const videoType = values.video_type || 'youtube';
  
  if (videoType === 'youtube') {
    const youtubeUrl = String(values.youtube_url || '').trim();
    if (!youtubeUrl) {
      errors.youtube_url = 'YouTube URL is required.';
    } else {
      const videoId = extractYouTubeId(youtubeUrl);
      if (!videoId) {
        errors.youtube_url = 'Invalid YouTube URL format.';
      }
    }
  }

  const displayOrder = values.display_order;
  if (displayOrder === '' || displayOrder === null || displayOrder === undefined) {
    errors.display_order = 'Display order is required.';
  } else {
    const order = Number(displayOrder);
    if (!Number.isFinite(order) || !Number.isInteger(order) || order < 0) {
      errors.display_order = 'Display order must be an integer ≥ 0.';
    }
  }

  return errors;
}

function buildPayload(values) {
  const videoType = values.video_type || 'youtube';
  let youtubeVideoId = null;
  let thumbnailUrl = values.thumbnail_url;
  
  if (videoType === 'youtube' && values.youtube_url) {
    youtubeVideoId = extractYouTubeId(values.youtube_url);
    thumbnailUrl = thumbnailUrl || generateThumbnailUrl(youtubeVideoId);
  }
  
  let slug = values.slug;
  if (!slug) {
    slug = values.title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  }

  const payload = {
    title: String(values.title || '').trim(),
    slug: slug,
    short_description: String(values.short_description || '').trim(),
    video_type: videoType,
    display_order: Math.max(0, parseInt(values.display_order, 10) || 0),
  };

  if (videoType === 'youtube') {
    payload.youtube_url = String(values.youtube_url || '').trim();
    payload.youtube_video_id = youtubeVideoId;
  }

  if (thumbnailUrl) {
    payload.thumbnail_url = thumbnailUrl;
  }

  return payload;
}

function buildEditSnapshot(row) {
  return {
    title: String(row.title ?? '').trim(),
    slug: String(row.slug ?? '').trim(),
    short_description: String(row.short_description ?? '').trim(),
    video_type: row.video_type || 'youtube',
    youtube_url: String(row.youtube_url ?? '').trim(),
    youtube_video_id: row.youtube_video_id,
    thumbnail_url: row.thumbnail_url,
    display_order: Math.max(0, parseInt(row.display_order, 10) || 0),
  };
}

function buildPartialUpdate(current, baseline) {
  if (!baseline) return buildPayload(current);
  const patch = {};
  const s = (v) => String(v ?? '').trim();

  if (s(current.title) !== s(baseline.title)) patch.title = s(current.title);
  if (s(current.short_description) !== s(baseline.short_description)) {
    patch.short_description = s(current.short_description);
  }
  
  const curY = s(current.youtube_url);
  const baseY = s(baseline.youtube_url);
  if (curY !== baseY) {
    patch.youtube_url = curY;
    patch.youtube_video_id = extractYouTubeId(curY);
    patch.thumbnail_url = current.thumbnail_url || generateThumbnailUrl(patch.youtube_video_id);
  }

  if (current.thumbnail_url !== baseline.thumbnail_url) {
    patch.thumbnail_url = current.thumbnail_url || null;
  }

  const cOrder = Math.max(0, parseInt(current.display_order, 10) || 0);
  const bOrder = Math.max(0, parseInt(baseline.display_order, 10) || 0);
  if (cOrder !== bOrder) patch.display_order = cOrder;

  if (s(current.slug) !== s(baseline.slug)) patch.slug = s(current.slug);

  return patch;
}

const defaultForm = () => ({
  title: '',
  slug: '',
  short_description: '',
  video_type: 'youtube',
  youtube_url: '',
  thumbnail_url: '',
  display_order: 0,
});

function BlogManagement({ showMessage }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [formModal, setFormModal] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [formValues, setFormValues] = useState(defaultForm);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [banner, setBanner] = useState({ text: '', type: '' });
  const [videoFile, setVideoFile] = useState(null);
  const searchTimer = useRef(null);
  const editBaselineRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setListError('');
    try {
      const data = await listBlogs();
      setRows(data.map(normalizeBlog));
    } catch (e) {
      setListError(e.message || 'Could not load blogs.');
      setRows([]);
      if (e.status === 401 && showMessage) {
        showMessage('Session expired or unauthorized. Please sign in again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  }, [showMessage]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setDebouncedSearch(searchInput.trim().toLowerCase());
    }, DEBOUNCE_MS);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [searchInput]);

  const filteredRows = useMemo(() => {
    let list = rows;

    if (statusFilter === 'active') {
      list = list.filter((r) => r.is_active);
    } else if (statusFilter === 'inactive') {
      list = list.filter((r) => !r.is_active);
    }

    if (!debouncedSearch) return list;

    return list.filter((r) => {
      const hay = [r.title, r.short_description].join(' ').toLowerCase();
      return hay.includes(debouncedSearch);
    });
  }, [rows, statusFilter, debouncedSearch]);

  const openCreate = () => {
    editBaselineRef.current = null;
    setFormValues(defaultForm());
    setFieldErrors({});
    setVideoFile(null);
    setFormModal({ mode: 'create' });
  };

  const openEdit = (row) => {
    editBaselineRef.current = buildEditSnapshot(row);
    setFormValues({
      title: row.title,
      slug: row.slug,
      short_description: row.short_description,
      video_type: row.video_type || 'youtube',
      youtube_url: row.youtube_url,
      thumbnail_url: row.thumbnail_url,
      display_order: row.display_order,
    });
    setFieldErrors({});
    setVideoFile(null);
    setFormModal({ mode: 'edit', id: row.id });
  };

  const closeForm = () => {
    editBaselineRef.current = null;
    setFormModal(null);
    setFieldErrors({});
    setFormValues(defaultForm());
  };

  const setBannerMsg = (text, type = 'success') => {
    setBanner({ text, type });
    setTimeout(() => setBanner({ text: '', type: '' }), 4000);
  };

  const onSubmitForm = async (e) => {
    e.preventDefault();
    setFieldErrors({});

    const initialErrors = validateForm(formValues);
    if (Object.keys(initialErrors).length) {
      setFieldErrors(initialErrors);
      return;
    }

    // Validate video file if uploaded type is selected
    if (formValues.video_type === 'uploaded' && !videoFile) {
      setFieldErrors({ video: 'Video file is required for uploaded video type.' });
      return;
    }

    setSubmitting(true);
    try {
      if (formModal.mode === 'edit') {
        const baseline = editBaselineRef.current;
        const patch = buildPartialUpdate(formValues, baseline);
        if (Object.keys(patch).length === 0 && !videoFile) {
          setFieldErrors({ _form: 'No changes to save.' });
          return;
        }
        await updateBlog(formModal.id, patch, videoFile);
        setBannerMsg('Blog updated.', 'success');
        if (showMessage) showMessage('Blog updated successfully.', 'success');
      } else {
        const payload = buildPayload(formValues);
        await createBlog(payload, videoFile);
        setBannerMsg('Blog created.', 'success');
        if (showMessage) showMessage('Blog created successfully.', 'success');
      }
      closeForm();
      await load();
    } catch (err) {
      const msg = err.message || 'Request failed.';
      setFieldErrors({ _form: msg });
      if (showMessage) showMessage(msg, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const onToggle = async (row) => {
    const prev = rows.slice();
    const nextActive = !row.is_active;
    setRows((p) =>
      p.map((r) => (r.id === row.id ? { ...r, is_active: nextActive } : r))
    );
    try {
      const raw = await toggleBlog(row.id, nextActive);
      const updated = normalizeBlog(raw);
      if (updated) {
        setRows((p) => p.map((r) => (r.id === row.id ? { ...r, ...updated } : r)));
      } else {
        await load();
      }
      if (showMessage) showMessage('Status updated.', 'success');
    } catch (err) {
      setRows(prev);
      const msg = err.message || 'Toggle failed.';
      setBannerMsg(msg, 'error');
      if (showMessage) showMessage(msg, 'error');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setSubmitting(true);
    try {
      await deleteBlog(deleteTarget.id);
      setDeleteTarget(null);
      if (showMessage) showMessage('Blog removed.', 'success');
      await load();
    } catch (err) {
      const msg = err.message || 'Delete failed.';
      if (showMessage) showMessage(msg, 'error');
      setBannerMsg(msg, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const formMode = formModal?.mode;
  const previewThumbnail = formValues.youtube_url 
    ? generateThumbnailUrl(extractYouTubeId(formValues.youtube_url))
    : null;

  return (
    <Container>
      <TitleRow>
        <PageTitle>Blog management</PageTitle>
        <Button type="button" onClick={openCreate}>
          Add blog
        </Button>
      </TitleRow>

      {banner.text && (
        <Message $success={banner.type === 'success'} $error={banner.type === 'error'}>
          {banner.text}
        </Message>
      )}

      <Toolbar>
        <ToolbarLeft>
          <Field>
            <ToolbarLabel htmlFor="blog-search">Search</ToolbarLabel>
            <SearchInput
              id="blog-search"
              placeholder="Title, description"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              autoComplete="off"
            />
          </Field>
          <Field>
            <ToolbarLabel htmlFor="blog-filter">Status</ToolbarLabel>
            <FilterSelect
              id="blog-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </FilterSelect>
          </Field>
        </ToolbarLeft>
      </Toolbar>

      {listError && (
        <Message $error>
          {listError}
        </Message>
      )}

      <TableWrap>
        <Table>
          <thead>
            <tr>
              <Th>Thumbnail</Th>
              <Th>Title</Th>
              <th>Description</th>
              <Th>YouTube URL</Th>
              <Th>Order</Th>
              <Th>Status</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <Tr>
                <Td colSpan={7}>
                  <EmptyState>Loading…</EmptyState>
                </Td>
              </Tr>
            ) : filteredRows.length === 0 ? (
              <Tr>
                <Td colSpan={7}>
                  <EmptyState>No blogs match the current filters.</EmptyState>
                </Td>
              </Tr>
            ) : (
              filteredRows.map((row) => (
                <Tr key={row.id}>
                  <Td>
                    {row.thumbnail_url ? (
                      <Thumbnail src={row.thumbnail_url} alt="" loading="lazy" />
                    ) : (
                      <div style={{ width: 80, height: 45, background: 'rgba(102, 126, 234, 0.2)', borderRadius: 6 }} />
                    )}
                  </Td>
                  <Td>{row.title}</Td>
                  <Td>{row.short_description.substring(0, 100)}...</Td>
                  <Td>
                    {row.video_type === 'uploaded' ? (
                      <span style={{ color: '#667eea', fontSize: '0.8rem' }}>Uploaded Video</span>
                    ) : (
                      <a href={row.youtube_url} target="_blank" rel="noopener noreferrer" style={{ color: '#667eea', textDecoration: 'none' }}>
                        Watch
                      </a>
                    )}
                  </Td>
                  <Td>{row.display_order}</Td>
                  <Td>
                    <StatusPill $active={row.is_active}>
                      {row.is_active ? 'Active' : 'Inactive'}
                    </StatusPill>
                  </Td>
                  <Td>
                    <ActionsCell>
                      <SecondaryButton
                        type="button"
                        onClick={() => openEdit(row)}
                      >
                        Edit
                      </SecondaryButton>
                      <SecondaryButton
                        type="button"
                        onClick={() => onToggle(row)}
                        disabled={submitting}
                      >
                        {row.is_active ? 'Deactivate' : 'Activate'}
                      </SecondaryButton>
                      <DangerButton
                        type="button"
                        onClick={() => setDeleteTarget(row)}
                      >
                        Delete
                      </DangerButton>
                    </ActionsCell>
                  </Td>
                </Tr>
              ))
            )}
          </tbody>
        </Table>
      </TableWrap>

      {formModal && (
        <Modal
          onClick={closeForm}
          role="presentation"
        >
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalTitle>
              {formMode === 'create' ? 'Add blog' : 'Edit blog'}
            </ModalTitle>
            {fieldErrors._form && <FieldError style={{ display: 'block', marginBottom: 12 }}>{fieldErrors._form}</FieldError>}
            <form onSubmit={onSubmitForm}>
              <FormGrid>
                <FormGroup>
                  <Label htmlFor="f-title">Title *</Label>
                  <Input
                    id="f-title"
                    value={formValues.title}
                    onChange={(e) => setFormValues((v) => ({ ...v, title: e.target.value }))}
                    autoComplete="off"
                  />
                  {fieldErrors.title && <FieldError>{fieldErrors.title}</FieldError>}
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="f-slug">Slug (optional)</Label>
                  <Input
                    id="f-slug"
                    value={formValues.slug}
                    onChange={(e) => setFormValues((v) => ({ ...v, slug: e.target.value }))}
                    placeholder="Auto-generated from title if empty"
                    autoComplete="off"
                  />
                  {fieldErrors.slug && <FieldError>{fieldErrors.slug}</FieldError>}
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="f-short-description">Short Description *</Label>
                  <TextArea
                    id="f-short-description"
                    value={formValues.short_description}
                    onChange={(e) => setFormValues((v) => ({ ...v, short_description: e.target.value }))}
                    placeholder="Brief description for the blog card"
                  />
                  {fieldErrors.short_description && <FieldError>{fieldErrors.short_description}</FieldError>}
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="f-video-type">Video Type *</Label>
                  <VideoTypeSelect
                    id="f-video-type"
                    value={formValues.video_type}
                    onChange={(e) => setFormValues((v) => ({ ...v, video_type: e.target.value }))}
                  >
                    <option value="youtube">YouTube</option>
                    <option value="uploaded">Upload Video</option>
                  </VideoTypeSelect>
                </FormGroup>

                {formValues.video_type === 'youtube' ? (
                  <FormGroup>
                    <Label htmlFor="f-youtube-url">YouTube URL *</Label>
                    <Input
                      id="f-youtube-url"
                      value={formValues.youtube_url}
                      onChange={(e) => setFormValues((v) => ({ ...v, youtube_url: e.target.value }))}
                      placeholder="https://www.youtube.com/watch?v=..."
                      autoComplete="off"
                    />
                    {fieldErrors.youtube_url && <FieldError>{fieldErrors.youtube_url}</FieldError>}
                    {previewThumbnail && (
                      <ThumbnailPreview src={previewThumbnail} alt="Thumbnail preview" />
                    )}
                  </FormGroup>
                ) : (
                  <FormGroup>
                    <Label>Upload Video *</Label>
                    <FileInputWrapper>
                      <FileInput
                        id="f-video-file"
                        type="file"
                        accept="video/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            setVideoFile(file);
                          }
                        }}
                      />
                      <FileInputLabel htmlFor="f-video-file">
                        Choose Video File
                      </FileInputLabel>
                    </FileInputWrapper>
                    {videoFile && (
                      <FileInfo>
                        Selected: {videoFile.name} ({(videoFile.size / (1024 * 1024)).toFixed(2)} MB)
                      </FileInfo>
                    )}
                    {fieldErrors.video && <FieldError>{fieldErrors.video}</FieldError>}
                  </FormGroup>
                )}

                <FormGroup>
                  <Label htmlFor="f-thumbnail-url">Thumbnail URL (optional)</Label>
                  <Input
                    id="f-thumbnail-url"
                    value={formValues.thumbnail_url}
                    onChange={(e) => setFormValues((v) => ({ ...v, thumbnail_url: e.target.value }))}
                    placeholder="Auto-generated from YouTube if empty"
                    autoComplete="off"
                  />
                  {fieldErrors.thumbnail_url && <FieldError>{fieldErrors.thumbnail_url}</FieldError>}
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="f-display-order">Display Order *</Label>
                  <Input
                    id="f-display-order"
                    type="number"
                    value={formValues.display_order}
                    onChange={(e) => setFormValues((v) => ({ ...v, display_order: e.target.value }))}
                    min="0"
                  />
                  {fieldErrors.display_order && <FieldError>{fieldErrors.display_order}</FieldError>}
                </FormGroup>
              </FormGrid>

              <ButtonRow>
                <GhostButton type="button" onClick={closeForm}>
                  Cancel
                </GhostButton>
                <PrimaryModalButton type="submit" disabled={submitting}>
                  {submitting ? 'Saving...' : formMode === 'create' ? 'Create Blog' : 'Update Blog'}
                </PrimaryModalButton>
              </ButtonRow>
            </form>
          </ModalCard>
        </Modal>
      )}

      {deleteTarget && (
        <Modal onClick={() => setDeleteTarget(null)} role="presentation">
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Confirm Delete</ModalTitle>
            <p style={{ color: '#4a5568', marginBottom: '1.5rem' }}>
              Are you sure you want to delete "{deleteTarget.title}"? This will deactivate the blog.
            </p>
            <ButtonRow>
              <GhostButton onClick={() => setDeleteTarget(null)}>
                Cancel
              </GhostButton>
              <DangerButton onClick={confirmDelete} disabled={submitting}>
                {submitting ? 'Deleting...' : 'Delete'}
              </DangerButton>
            </ButtonRow>
          </ModalCard>
        </Modal>
      )}
    </Container>
  );
}

export default BlogManagement;
